import { useQuery } from '@tanstack/react-query';
import { useParams, useRouteContext } from '@tanstack/react-router';
import { EmptyState, ErrorState } from '@bb/ui';
import { ApiError } from '../../api/client.ts';

const when = (iso: string | null) => (iso ? `${iso.slice(0, 16).replace('T', ' ')} UTC` : '—');
const STATUS: Record<string, string> = { queued: 'Queued', running: 'Making the file…', completed: 'Ready', failed: 'Failed', expired: 'Expired' };

/** Settings → My exports (BB-50): the CSVs this person asked for, their state, and downloads while they last (4 h). */
export function ExportsPage() {
  const { environmentId } = useParams({ from: '/env/$environmentId/settings/exports' });
  const { api } = useRouteContext({ from: '/env/$environmentId/settings/exports' });
  const list = useQuery({
    queryKey: ['exports', environmentId],
    queryFn: () => api.myExports(environmentId),
    refetchInterval: (q) => (q.state.data?.items.some((e) => e.status === 'queued' || e.status === 'running') ? 3_000 : false),
  });
  return (
    <section>
      <h1>My exports</h1>
      <p className="hint">Files are kept for 4 hours, then removed. Every request and download is in the audit log.</p>
      {list.isPending ? (
        <p aria-busy="true">Loading…</p>
      ) : list.isError ? (
        <ErrorState title="Exports could not be loaded" message={list.error instanceof ApiError ? list.error.message : 'Try again.'} onRetry={() => void list.refetch()} />
      ) : list.data.items.length === 0 ? (
        <EmptyState title="No exports" description="Export the users of a segment from its page." />
      ) : (
        <table className="table" aria-label="Exports">
          <thead>
            <tr>
              <th scope="col">Of</th>
              <th scope="col">Status</th>
              <th scope="col">Rows</th>
              <th scope="col">Asked</th>
              <th scope="col">Available until</th>
              <th scope="col">File</th>
            </tr>
          </thead>
          <tbody>
            {list.data.items.map((e) => (
              <tr key={e.id}>
                <td>
                  {e.kind === 'segment' ? (e.segmentName ?? 'Segment') : 'Global control group'}
                  <div className="hint">
                    {e.columns.join(', ')}
                    {e.droppedColumns.length > 0 && ` · left out: ${e.droppedColumns.join(', ')}`}
                  </div>
                </td>
                <td>
                  {STATUS[e.status] ?? e.status}
                  {e.error && <div className="hint">{e.error}</div>}
                </td>
                <td>{e.rowCount?.toLocaleString('en-US') ?? '—'}</td>
                <td>{when(e.createdAt)}</td>
                <td>{when(e.expiresAt)}</td>
                <td>{e.downloadPath ? <a href={e.downloadPath}>Download CSV</a> : '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}
